/**
 * Runtime Configuration System
 * 
 * این فایل config.json رو در runtime لود میکنه
 * برای کوبرنتیز: ConfigMap مقدار BASE_URL رو تنظیم میکنه
 * برای داکر لوکال: از مقدار پیش‌فرض استفاده میشه
 */

export interface RuntimeConfig {
  BASE_URL: string;
  MOBILE_BASE_URL?: string;
}

let config: RuntimeConfig | null = null;
let configPromise: Promise<RuntimeConfig> | null = null;

/**
 * Load config from /config.json
 * این فایل توسط ConfigMap در کوبرنتیز جایگزین میشه
 */
export async function loadConfig(): Promise<RuntimeConfig> {
  if (config) {
    return config;
  }

  if (configPromise) {
    return configPromise;
  }

  configPromise = (async () => {
    try {
      // Add timeout to prevent blocking
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
      
      const response = await fetch('/config.json', { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`Failed to load config: ${response.status}`);
      }
      config = await response.json();
      console.log('✅ [RuntimeConfig] Loaded:', config);
      return config!;
    } catch (error) {
      console.warn('⚠️ [RuntimeConfig] Failed to load config.json, using defaults:', error);
      config = { 
        BASE_URL: '',
        MOBILE_BASE_URL: 'https://mybooka.ir'
      };
      return config;
    }
  })();

  return configPromise;
}

/**
 * Get config synchronously (must call loadConfig first)
 */
export function getConfig(): RuntimeConfig {
  if (!config) {
    console.warn('⚠️ [RuntimeConfig] Config not loaded yet, using defaults');
    return { 
      BASE_URL: '',
      MOBILE_BASE_URL: 'https://mybooka.ir'
    };
  }
  return config;
}

/**
 * Get BASE_URL (platform-aware)
 */
export function getBaseUrl(): string {
  const cfg = getConfig();
  
  // Check if running on native mobile platform (Capacitor)
  const isNative = typeof window !== 'undefined' && 
                   (window as any).Capacitor?.isNativePlatform?.() === true;
  
  // Use MOBILE_BASE_URL for native apps, otherwise use BASE_URL
  if (isNative && cfg.MOBILE_BASE_URL) {
    console.log('📱 [RuntimeConfig] Using MOBILE_BASE_URL:', cfg.MOBILE_BASE_URL);
    return cfg.MOBILE_BASE_URL;
  }

  // In local Docker development, Vite serves the client on port 3000 while
  // the API Gateway is exposed on port 80. An empty BASE_URL must therefore
  // target the gateway, not the Vite development server.
  if (!isNative && !cfg.BASE_URL && typeof window !== 'undefined' && window.location.port === '3000') {
    const localGatewayUrl = `${window.location.protocol}//${window.location.hostname}`;
    console.log('🛠️ [RuntimeConfig] Using local API Gateway:', localGatewayUrl);
    return localGatewayUrl;
  }

  console.log('🌐 [RuntimeConfig] Using BASE_URL:', cfg.BASE_URL);
  return cfg.BASE_URL;
}

/**
 * Build service URLs from BASE_URL
 */
export function getServiceUrls() {
  const baseUrl = getBaseUrl();
  return {
    USER_SERVICE: `${baseUrl}/api/v1/user-service`,
    BOOK_SERVICE: `${baseUrl}/api/v1/book-service`,
    PAYMENT_SERVICE: `${baseUrl}/api/v1/payment-service`,
    AI_SERVICE: `${baseUrl}/api/v1/ai-service`,
    GAMIFICATION_SERVICE: `${baseUrl}/api/v1/gamification-service`,
  };
}
